//  ----------      START   IMPORT  BLOCK       ----------
import { useState, useMemo, useCallback } from 'react';
//  Widgets
import UpdateTreatmentModal from './UpdateTreatmentModal/UpdateTreatmentModal';
import NumberImage from './NumberImage';
//  Context
import { useLanguage } from '../../../../../API/Context/LanguageContext';
import { useUserDiagnoses } from '../../../../../API/Hooks/useUserDiagnoses'
import { useUserPrescriptions } from '../../../../../API/Hooks/useUserPrescriptions'
//  Config
import { VocabStatusDisplay } from '../../../../../ConfigFiles/multilingual/VocabStatusDisplay';
import {VocabChronicCategories as ctgry_vocab} from '../../../../../ConfigFiles/multilingual/VocabChronicCategories';
import './StatusDisplay.css';
//  ----------      END   IMPORT  BLOCK       ----------

const StatusDisplay = () => {
  //  Context
  const { lan } = useLanguage()
  const { diagnoses } = useUserDiagnoses()
  const { active_prescriptions } = useUserPrescriptions()

  // 1. Transient UI state remains as useState
  const [showModal, setShowModal] = useState(false);

  // Use useCallback for stable function reference
  const toggleModal = useCallback(() => setShowModal(prev => !prev), []);

  // 2. DERIVED STATE: Use useMemo to calculate the list of chronic conditions
  const activeCronic = useMemo(() => {
    // Safely access the contained_affections array, defaulting to []
    return diagnoses?.contained_affections ?? [];
  }, [diagnoses]); // Recalculates only when the diagnoses object changes

  // 3. DERIVED STATE: Use useMemo to calculate medication enabled status
  const medicationEnabled = useMemo(() => {
    // If active_prescriptions is guaranteed to be an array, this is the cleanest way
    return active_prescriptions.length > 0;
  }, [active_prescriptions]); // Recalculates only when the prescriptions array changes

  // 4. EFFICIENT COMPUTATION: Group items using useMemo
  const groupedIcons = useMemo(() => {
    return activeCronic.filter(item => item.chronic).reduce((acc, item) => {
      // Ensure item.category is a valid key before accessing the language object
      if (item.category !== null && item.category !== undefined) {
        if (!acc[item.category]) {
          acc[item.category] = [];
        }
        acc[item.category].push(item.name);
      }
      return acc;
    }, {});
  }, [activeCronic]); // Recalculates only when the activeCronic list changes

  // 5. MEMOIZED CONSTANTS: Use useMemo for the constant, language-dependent list
  const chronic_categories = useMemo(() => ([
    ctgry_vocab.cardiovascular_diseases[lan],
    ctgry_vocab.respiratory_diseases[lan],
    ctgry_vocab.diabetes[lan],
    ctgry_vocab.kidney_diseases[lan],
    ctgry_vocab.neurological_disorders[lan],
    ctgry_vocab.cancer[lan],
    ctgry_vocab.musculoskeletal_disorders[lan],
    ctgry_vocab.autoimmune_diseases[lan],
    ctgry_vocab.metabolic_disorders[lan],
    ctgry_vocab.digestive_system_disorders[lan],
    ctgry_vocab.mental_health_conditions[lan],
    ctgry_vocab.infectious_diseases[lan],
    ctgry_vocab.skin_conditions[lan],
  ]), [lan]); // Recalculates only when the language changes


  return (
    <div className="vertical-icon-container">
      <NumberImage
            className="icon-hover-info"
            number={0}
            alt={VocabStatusDisplay.medication[lan]}
            width={'25px'}
            height={'25px'}
            onClick={medicationEnabled ? toggleModal : null}
            enabled={medicationEnabled}
            tooltipText={medicationEnabled ? VocabStatusDisplay.active_medication[lan] : VocabStatusDisplay.no_active_medication[lan]}
            //  Show if user is medicated by style.
          />

      <>
          {Object.keys(groupedIcons).map((category) => (
              <NumberImage
              key={category} 
              className="icon-hover-info"
              number={category}
              alt={chronic_categories[category][lan]} // Use category index for chronic_categories array
              width={'25px'}
              height={'25px'}
              tooltipText={groupedIcons[category].join('\n')}
              />

          ))}
        </>

      <UpdateTreatmentModal showModal={showModal} toggleModal={toggleModal}/>
    </div>
  );
};

export default StatusDisplay;